<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  src: { type: String, required: true },
  alt: { type: String, required: true },
  width: { type: [Number, String], default: null },
  height: { type: [Number, String], default: null },
  aspectRatio: {
    type: String,
    default: null,
    validator: (v) => ['square', '16/9', '4/3', '3/2'].includes(v)
  },
  fit: {
    type: String,
    default: 'contain',
    validator: (v) => ['cover', 'contain', 'fill'].includes(v)
  },
  errorMessage: { type: String, default: 'Failed to load image' }
})

const emit = defineEmits(['load', 'error'])

const loading = ref(true)
const error = ref(false)

const sizeStyle = computed(() => {
  const s = { '--image-fit': props.fit }
  if (props.width) s.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  if (props.height) s.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  return s
})

function handleLoad() {
  loading.value = false
  error.value = false
  emit('load')
}

function handleError() {
  loading.value = false
  error.value = true
  emit('error')
}
</script>

<template>
  <div
    class="image-container"
    :class="{
      'is-loading': loading,
      'has-error': error,
      [`aspect-${aspectRatio?.replace('/', '-')}`]: aspectRatio
    }"
    :style="sizeStyle"
  >
    <img
      v-if="!error"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :class="{ 'fade-in': !loading }"
      loading="lazy"
      @load="handleLoad"
      @error="handleError"
    />

    <div v-if="loading" class="image-placeholder image-placeholder--loading" :style="sizeStyle">
      <span class="spinner" />
    </div>

    <div v-if="error" class="image-placeholder image-placeholder--error" :style="sizeStyle">
      <span class="error-icon">!</span>
      <span class="error-text">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.image-container {
  position: relative;
  overflow: hidden;
  border-radius: $radius;
  min-height: 80px;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: var(--image-fit, contain);
    opacity: 0;
    @include transition(opacity);

    &.fade-in { opacity: 1; }
  }

  &.aspect-square { aspect-ratio: 1; }
  &.aspect-16-9   { aspect-ratio: 16 / 9; }
  &.aspect-4-3    { aspect-ratio: 4 / 3; }
  &.aspect-3-2    { aspect-ratio: 3 / 2; }
}

.image-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-fill);

  &--error {
    flex-direction: column;
    gap: $space-2;
    color: var(--color-danger);
  }
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--color-primary);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.error-icon {
  font-size: 20px;
  font-weight: 700;
}

.error-text {
  font-size: 13px;
  color: var(--color-text-muted);
  text-align: center;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
